import cn from 'classnames';
import { useEffect } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { useScrollToSearchResultsOnActiveFilterChange } from 'src/hooks/useSearchPage';
import { useSite } from 'src/hooks/useSite';
import { useSiteSearchSubmitContext } from 'src/hooks/useSiteSearchSubmitContext';

import ActiveFiltersList from 'components/BaseHelpers/ActiveFiltersList';
import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import SearchFilterAccordions from 'components/BaseHelpers/SearchFilterAccordions';
import SortByControl from 'components/BaseHelpers/SortByControl';
import StartIndex from 'components/BaseHelpers/StartIndex';
import StopIndex from 'components/BaseHelpers/StopIndex';
import Text from 'components/BaseHelpers/Text';
import TwoColumnLayout from 'components/BaseHelpers/TwoColumnLayout';

import type { ComponentProps } from '../../../types/component-props';
import type { CoreSiteSearchData } from '../../../types/components';
import InfiniteScrollLoadMore from './components/InfiniteScrollLoadMore';
import SiteSearchCombobox from './components/SiteSearchCombobox';
import ResultsListing from './components/SiteSearchResultsListing';
import SiteSearchResultsSummary from './components/SiteSearchResultsSummary';
import { SiteSearchListKey, siteSearchSortOptions, useSiteSearch } from './useSiteSearch';

import styles from './CoreSiteSearch.module.scss';

export type CoreSiteSearchProps = ComponentProps & CoreSiteSearchData;

const CoreSiteSearch = (props: CoreSiteSearchProps): JSX.Element => {
  const {
    filters,
    keywordSearchPlaceholder,
    peopleResultsPerPageSitewide: peoplePreviewResultsPerPage,
    servicesResultsPerPageSitewide: servicesPreviewResultsPerPage,
    newsInsightsResultsPerPageSitewide: newsPreviewResultsPerPage,
    resourcesResultsPerPageSitewide: resourcesPreviewResultsPerPage,
    peopleResultsPerPageTab: peopleDetailResultsPerPage,
    servicesResultsPerPageTab: servicesDetailResultsPerPage,
    newsInsightsResultsPerPageTab: newsDetailResultsPerPage,
    resourcesResultsPerPageTab: resourcesDetailResultsPerPage,
    peopleSectionHeading,
    servicesSectionHeading,
    newsInsightsSectionHeading,
    resourcesSectionHeading,
    errorMessage,
    noResultsMessage,
    enterSearchTermMessage,
    typeaheadResultsCount,
  } = props.fields;

  const {
    filtersLabel,
    queryFilterLabel,
    serviceFilterLabel,
    locationFilterLabel,
    clearAllLabel,
    recentSearchesLabel,
    seeMoreLabel,
    sortByLabel,
    computeResultsCountLabel,
  } = useDictionary();
  const { isEastAsiaSite } = useSite();
  const { registerSiteSearchSubmitHandler } = useSiteSearchSubmitContext();

  const search = useSiteSearch({
    previewResultsPerPage: {
      people: peoplePreviewResultsPerPage,
      services: servicesPreviewResultsPerPage,
      newsinsights: newsPreviewResultsPerPage,
      resources: resourcesPreviewResultsPerPage,
    },
    detailResultsPerPage: {
      people: peopleDetailResultsPerPage,
      services: servicesDetailResultsPerPage,
      newsinsights: newsDetailResultsPerPage,
      resources: resourcesDetailResultsPerPage,
    },
    filters,
  });

  useEffect(
    () => registerSiteSearchSubmitHandler(search.submitQuery),
    [registerSiteSearchSubmitHandler, search.submitQuery]
  );

  const sectionDefinitions: Array<{ key: SiteSearchListKey; title: string }> = [
    { key: 'people', title: peopleSectionHeading },
    { key: 'services', title: servicesSectionHeading },
    { key: 'newsinsights', title: newsInsightsSectionHeading },
    { key: 'resources', title: resourcesSectionHeading },
  ];

  const resultSections = sectionDefinitions.map(({ key, title }) => ({
    key,
    title,
    list: search.overviewLists[key],
  }));

  const focusedSection = search.activeTab
    ? {
        key: search.activeTab,
        title: sectionDefinitions.find((section) => section.key === search.activeTab)?.title ?? '',
        list: search.activeDetailList,
      }
    : null;

  const totalVisibleResults =
    search.mode === 'detail'
      ? (search.activeDetailList?.items.length ?? 0)
      : resultSections.reduce((count, section) => count + section.list.items.length, 0);
  const hasAnyItems = totalVisibleResults > 0;
  const hasAnyError =
    search.mode === 'detail'
      ? Boolean(search.activeDetailList?.error)
      : resultSections.some((section) => Boolean(section.list.error));
  const isLoadingInitialResults =
    totalVisibleResults === 0 &&
    (search.mode === 'detail'
      ? Boolean(search.activeDetailList?.loading)
      : resultSections.some((section) => section.list.loading));
  const resultsRef = useScrollToSearchResultsOnActiveFilterChange(search.activeFilters);

  return (
    <>
      <StopIndex />
      <section className={cn(styles.main, 'container-12')}>
        <TwoColumnLayout
          leftChildren={
            <div className={styles['left-column']}>
              <SiteSearchCombobox
                placeholder={keywordSearchPlaceholder}
                queryDraft={search.queryDraft}
                recentSearchesLabel={recentSearchesLabel}
                typeaheadResultsCount={typeaheadResultsCount}
                setQueryDraft={search.setQueryDraft}
                submitQuery={search.submitQuery}
              />
              {search.query && focusedSection && focusedSection.key === 'newsinsights' && (
                <SortByControl
                  onChange={search.setSort}
                  value={search.sort}
                  sortOptions={siteSearchSortOptions}
                />
              )}
              {/* The hook-owned predicate is the SOLE gate for the accordions (single owner) —
                  do not re-encode any part of the condition (e.g. `search.query`)
                  here; the facets fetch gate moves with it if this condition ever changes. */}
              {search.canShowFilterAccordions && (
                <SearchFilterAccordions
                  filterConfigs={filters}
                  filterState={search.filters}
                  sectionLabel={filtersLabel}
                  servicesFilterName="services"
                  facetState={search.facetState}
                />
              )}
            </div>
          }
          rightChildren={
            <div ref={resultsRef} className={styles['right-column']}>
              {search.query && (
                <>
                  {search.hasActiveFilters && (
                    <>
                      <SiteSearchResultsSummary
                        activeKey={search.activeTab}
                        lists={search.overviewLists}
                        labels={{
                          people: peopleSectionHeading,
                          services: servicesSectionHeading,
                          newsinsights: newsInsightsSectionHeading,
                          resources: resourcesSectionHeading,
                        }}
                        listKeys={
                          isEastAsiaSite ? ['people', 'newsinsights', 'resources'] : undefined
                        }
                        onSelectOverview={search.showOverview}
                        onSelectTab={search.openDetailTab}
                      />
                      <ActiveFiltersList
                        labels={{
                          query: queryFilterLabel,
                          services: serviceFilterLabel,
                          offices: locationFilterLabel,
                          'clear-all': clearAllLabel,
                        }}
                        activeFilters={search.activeFilters}
                        sortFilter={
                          search.sort === 'date'
                            ? {
                                sectionLabel: sortByLabel,
                                sortLabel: search.sort,
                                clearSort: () => search.setSort('relevance'),
                              }
                            : undefined
                        }
                        clearAll={search.clearAll}
                      />
                    </>
                  )}

                  {Boolean(hasAnyError) && !hasAnyItems && (
                    <RichText className="body-sm" field={errorMessage} />
                  )}

                  {!isLoadingInitialResults && !hasAnyItems && !hasAnyError && (
                    <RichText className="body-sm" field={noResultsMessage} />
                  )}

                  {(hasAnyItems || hasAnyError || isLoadingInitialResults) &&
                    search.mode === 'overview' && (
                      <div className={styles.sections}>
                        {resultSections.map(({ key, title, list }) => {
                          if (list.items.length === 0 && !list.error) {
                            return null;
                          }

                          return (
                            <div className={styles.section} key={key} data-section={key}>
                              <div className={styles['section-header']}>
                                <div>
                                  <Text
                                    tag="h2"
                                    className={cn('heading-h4', styles['section-title'])}
                                    field={title}
                                  />
                                </div>
                                {search.hasActiveFilters && (
                                  <div className={cn('body-sm', styles['section-meta'])}>
                                    {computeResultsCountLabel(
                                      list.items.length,
                                      list.totalCount ?? list.items.length
                                    )}
                                  </div>
                                )}
                              </div>

                              {Boolean(list.error) && list.items.length === 0 && (
                                <RichText className="body-sm" field={errorMessage} />
                              )}

                              {list.items.length > 0 && (
                                <ResultsListing
                                  items={list.items}
                                  resultsToShow={list.items.length}
                                />
                              )}

                              {((list.totalCount ?? 0) > list.items.length || list.hasMore) && (
                                <div>
                                  <Cta
                                    tag="button"
                                    onClick={() => search.openDetailTab(key)}
                                    type="button"
                                  >
                                    {seeMoreLabel}
                                  </Cta>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {(hasAnyItems || hasAnyError || isLoadingInitialResults) &&
                    search.mode === 'detail' &&
                    focusedSection?.list && (
                      <div className={styles.sections}>
                        <div className={styles.section} data-section={focusedSection.key}>
                          <div className={styles['section-header']}>
                            <div>
                              <Text
                                tag="h2"
                                className={cn('heading-h4', styles['section-title'])}
                                field={focusedSection.title}
                              />
                            </div>
                            {search.hasActiveFilters && (
                              <div className={cn('body-sm', styles['section-meta'])}>
                                {computeResultsCountLabel(
                                  focusedSection.list.items.length,
                                  focusedSection.list.totalCount ?? focusedSection.list.items.length
                                )}
                              </div>
                            )}
                          </div>

                          {Boolean(focusedSection.list.error) &&
                            focusedSection.list.items.length === 0 && (
                              <RichText className="body-sm" field={errorMessage} />
                            )}

                          {focusedSection.list.items.length > 0 && (
                            <ResultsListing
                              items={focusedSection.list.items}
                              resultsToShow={focusedSection.list.items.length}
                            />
                          )}

                          {focusedSection.list.hasMore && (
                            <InfiniteScrollLoadMore
                              searchMode={search.mode}
                              activeDetailList={focusedSection.list}
                            />
                          )}
                        </div>
                      </div>
                    )}
                </>
              )}
              {!search.query && (
                <div>
                  <RichText
                    className={cn('heading-h3', styles['welcome-message'])}
                    field={enterSearchTermMessage}
                  />
                </div>
              )}
            </div>
          }
        />
      </section>
      <StartIndex />
    </>
  );
};

export default CoreSiteSearch;
